import { Check, ChevronUp, ExternalLink, Globe, X } from "lucide-mui"
import React, { useEffect, useState } from "react"
import { Link } from "react-router-dom"

import { AVAILABLE_LOCALES } from "@/constants/locales"
import { useLocale } from "@/contexts/locale"
import i18n from "@/i18n"

const TRANSITION_MS = 250

const STYLES = {
  root: "fixed inset-0 z-50 sm:hidden",
  backdrop: "absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-[250ms] ease-out cursor-default",
  panel:
    "absolute right-0 top-0 flex h-full w-1/2 max-w-[50%] flex-col bg-white shadow-[-8px_0_24px_rgba(0,0,0,0.12)] transition-transform duration-[250ms] ease-out",
  closeRow: "flex items-center justify-end px-5 py-4",
  closeBtn: "flex h-6 w-6 items-center justify-center text-[#171717]",
  closeIcon: "h-5 w-5",
  divider: "h-px w-full bg-[#e5e5e5]",
  sectionHeader: "flex h-[50px] items-center justify-between px-5",
  sectionLeft: "flex items-center gap-[14px]",
  sectionIcon: "h-[18px] w-[18px] text-[#171717]",
  sectionLabel: "text-[14px] font-medium leading-none text-black",
  sectionChevron: "h-[12px] w-[12px] text-[#171717]",
  localeList: "flex flex-col",
  localeItem: "flex h-[27px] items-center justify-between px-5 text-[14px] leading-none text-[#454545]",
  localeItemSelected: "bg-[rgba(37,150,250,0.15)]",
  localeCheck: "h-[14px] w-[14px] text-[#2596fa]",
  businessLink: "flex items-center gap-[14px] px-5 py-4 text-[14px] leading-none text-[#171717] no-underline",
  businessIcon: "h-[18px] w-[18px] text-[#171717]",
} as const

type Props = {
  open: boolean
  onClose: () => void
}

const PublicMobileMenuDrawer: React.FC<Props> = ({ open, onClose }) => {
  const { locale, setLocale } = useLocale()
  const [mounted, setMounted] = useState(open)
  const [entered, setEntered] = useState(false)

  useEffect(() => {
    if (open) {
      setMounted(true)
      const id = window.requestAnimationFrame(() => setEntered(true))
      return () => window.cancelAnimationFrame(id)
    }

    setEntered(false)
    const timeout = window.setTimeout(() => setMounted(false), TRANSITION_MS)
    return () => window.clearTimeout(timeout)
  }, [open])

  useEffect(() => {
    if (!mounted) return undefined

    const original = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = original
    }
  }, [mounted])

  if (!mounted) return null

  const handleSelectLocale = (selectedLocale: string) => {
    setLocale(selectedLocale)
    onClose()
  }

  return (
    <div className={STYLES.root} role="dialog" aria-modal="true" data-testid="public-venue-mobile-menu">
      <button
        type="button"
        aria-label={i18n.t("app.actions.close")}
        className={STYLES.backdrop}
        style={{ opacity: entered ? 1 : 0 }}
        onClick={onClose}
        data-testid="public-venue-mobile-menu-backdrop"
      />
      <div
        className={STYLES.panel}
        style={{ transform: entered ? "translateX(0)" : "translateX(100%)" }}
        data-testid="public-venue-mobile-menu-panel"
      >
        <div className={STYLES.closeRow}>
          <button
            type="button"
            onClick={onClose}
            className={STYLES.closeBtn}
            aria-label={i18n.t("app.actions.close")}
            data-testid="public-venue-mobile-menu-close-btn"
          >
            <X className={STYLES.closeIcon} />
          </button>
        </div>
        <div className={STYLES.divider} />

        <div className={STYLES.sectionHeader}>
          <div className={STYLES.sectionLeft}>
            <Globe className={STYLES.sectionIcon} />
            <span className={STYLES.sectionLabel}>{i18n.t("app.public.venue.top_bar.language")}</span>
          </div>
          <ChevronUp className={STYLES.sectionChevron} />
        </div>

        <div className={STYLES.localeList} data-testid="public-venue-mobile-menu-locales">
          {AVAILABLE_LOCALES.map((loc) => {
            const selected = loc === locale
            return (
              <button
                key={loc}
                type="button"
                onClick={() => handleSelectLocale(loc)}
                className={`${STYLES.localeItem} ${selected ? STYLES.localeItemSelected : ""}`}
                data-testid={`public-venue-mobile-menu-locale-${loc}`}
              >
                <span>{i18n.t(`app.language_selector.languages.${loc}`)}</span>
                {!!selected && <Check className={STYLES.localeCheck} />}
              </button>
            )
          })}
        </div>

        <div className={STYLES.divider} style={{ marginTop: 16 }} />

        <Link
          to="/"
          onClick={onClose}
          className={STYLES.businessLink}
          data-testid="public-venue-mobile-menu-for-business-link"
        >
          <ExternalLink className={STYLES.businessIcon} />
          <span>
            <span className="font-semibold">{i18n.t("app.public.venue.footer.tabb_brand")}</span>{" "}
            <span>{i18n.t("app.public.venue.footer.tabb_for_business")}</span>
          </span>
        </Link>
      </div>
    </div>
  )
}

export default PublicMobileMenuDrawer
